import type { ApiError } from '@/api/client.types';
import { useStudentView } from '@/features/studentView/StudentViewProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import { useParams } from 'react-router';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { ComponentKebabMenu } from '@/features/content/componentActions/ComponentKebabMenu';
import { rosterQueryKey, useRoster } from '@/features/gradebook/useRoster';
import { useCoachDueProgress } from '@/features/kaiCoach/useCoachDueProgress';
import { useCourseStaffInvites } from '@/features/staffInvites/useCourseStaffInvites';

import { ComponentCard } from './ComponentCard';
import { createCourseHomeDragAnnouncements } from './courseHomeAnnouncements';
import { CourseControlPanel } from './CourseControlPanel';
import { CreateSectionFAB } from './CreateSectionFAB';
import { DueSoonSection } from './DueSoonSection';
import { EditCourseButton } from './EditCourseButton';
import { SectionDivider } from './SectionDivider';
import {
  applyCoachDueProgress,
  buildDueSoonItems,
  computeComponentDisplayNumbers,
  reorderCourseHomeItems,
} from './courseHomeUtils';
import {
  isAutoNumberingDisabled,
  isDueSoonSectionDisabled,
} from './courseDisplaySettings';
import {
  useCourse,
  type ComponentListItem,
  type RawComponent,
} from './useCourse';
import {
  buildUpdateCourseHomePayload,
  useUpdateCourseHome,
} from './useUpdateCourseHome';
import { useCourseControlPanelActions } from './useCourseControlPanelActions';
import styles from './CourseHomePage.module.css';

const INSTRUCTOR_CARD_DISMISSED_STORAGE_KEY =
  'kudu.courseHomepage.instructorCardDismissed';

function getChapterCountLabel(components: ComponentListItem[]) {
  const chapterCount = components.filter((item) => item.kind === 'component').length;

  return `${chapterCount} chapter${chapterCount === 1 ? '' : 's'} in this course.`;
}

function getErrorMessage(error: ApiError) {
  return error.message || 'Unable to load this course right now.';
}

function getComponentIds(components: ComponentListItem[]) {
  return components.map((component) => component.id);
}

function areIdsEqual(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

interface SortableCourseHomeItemProps {
  displayNumber: number | null;
  item: ComponentListItem;
  rowIndex: number;
  showStatusVisibilityIndicator: boolean;
}

function SortableCourseHomeItem({
  displayNumber,
  item,
  rowIndex,
  showStatusVisibilityIndicator,
}: SortableCourseHomeItemProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: item.id,
  });
  const dragHandle = (
    <button
      {...attributes}
      {...listeners}
      aria-label={`Reorder ${item.title}`}
      className={styles.dragHandle}
      type="button"
    >
      <GripVertical aria-hidden="true" size={18} strokeWidth={2} />
    </button>
  );

  if (item.kind === 'divider') {
    return (
      <div
        className={styles.dividerItem}
        data-dragging={isDragging ? 'true' : 'false'}
        data-kind={item.kind}
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
      >
        <SectionDivider dragHandle={dragHandle} item={item} />
      </div>
    );
  }

  return (
    <li
      className={styles.item}
      data-dragging={isDragging ? 'true' : 'false'}
      data-interactive={item.kind === 'component' ? 'true' : 'false'}
      data-kind={item.kind}
      data-separated={rowIndex > 0 ? 'true' : 'false'}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <ComponentCard
        displayNumber={displayNumber}
        dragHandle={dragHandle}
        item={item}
        showStatusVisibilityIndicator={showStatusVisibilityIndicator}
      />
    </li>
  );
}

export function CourseHomePage() {
  const { courseId } = useParams();
  const courseQuery = useCourse(courseId);
  const queryClient = useQueryClient();
  const isInstructor = courseQuery.data?.userRole === 'instructor';
  const rosterQuery = useRoster(courseId, { enabled: isInstructor });
  const updateCourseHome = useUpdateCourseHome(courseId);
  const controlPanelActions = useCourseControlPanelActions(courseId);
  const course = courseQuery.data;
  const staffInvites = useCourseStaffInvites(courseId, course?.instructorId);
  // Students join without this page knowing, so an instructor coming back to
  // the tab gets the count read again.
  useEffect(() => {
    if (!courseId || !isInstructor) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void queryClient.invalidateQueries({
          exact: true,
          queryKey: rosterQueryKey(courseId),
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [courseId, isInstructor, queryClient]);

  // The gradebook takes a cached roster as loaded, so invalidating would open it
  // on the count this page has until the new one arrived.
  const handleOpenRoster = () => {
    if (courseId) {
      void queryClient.resetQueries({
        exact: true,
        queryKey: rosterQueryKey(courseId),
      });
    }
  };

  // In student view, tell the banner whether visibility settings are hiding
  // any components so it can offer the display-only "show all content" toggle.
  const studentView = useStudentView();
  const hiddenComponentCount = course?.hiddenComponentCount ?? 0;
  const reportHiddenContent = studentView.reportHiddenContent;

  useEffect(() => {
    if (!studentView.active) {
      return undefined;
    }

    reportHiddenContent(hiddenComponentCount > 0);
    return () => reportHiddenContent(false);
  }, [studentView.active, hiddenComponentCount, reportHiddenContent]);
  // A student's coach deadlines they have already met leave Due Soon, and a
  // coach's row names their next one. Only students ask, only for coaches with
  // credit and a deadline listed; the homepage never waits on it.
  const coachProgressIds = useMemo(
    () =>
      (course?.components ?? [])
        .filter(
          (item) =>
            item.kind === 'component' &&
            (item.dueSoonLines ?? []).some(
              (line) => line.kind === 'coachSession' && line.creditEnabled,
            ),
        )
        .map((item) => item.id),
    [course?.components],
  );
  const coachProgress = useCoachDueProgress(
    courseId,
    coachProgressIds,
    course?.userRole === 'student' && coachProgressIds.length > 0,
  );
  const components = useMemo(
    () => applyCoachDueProgress(course?.components ?? [], coachProgress),
    [course?.components, coachProgress],
  );
  const [isEditing, setIsEditing] = useState(false);
  const [draftComponents, setDraftComponents] = useState<ComponentListItem[]>([]);
  const [isInstructorCardDismissed, setIsInstructorCardDismissed] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.localStorage.getItem(INSTRUCTOR_CARD_DISMISSED_STORAGE_KEY) === 'true',
  );
  const [actionAnnouncement, setActionAnnouncement] = useState('');
  // A move is announced by the drag's own live region, by the item's title
  // and place: dnd-kit's words named its raw id (#173).
  const dragAnnouncements = useMemo(
    () => createCourseHomeDragAnnouncements(draftComponents),
    [draftComponents],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const activeComponents = isEditing ? draftComponents : components;
  // Display settings are owned by the control panel's Display Settings dialog,
  // so the homepage only ever READS the fetched course. There is no draft to
  // prefer, and edit mode no longer writes them.
  const activeSettings = course?.courseSettings ?? {};
  const activeComponentIds = getComponentIds(activeComponents);
  const fetchedComponentIds = course?.componentUids ?? getComponentIds(components);
  const isOrderDirty = isEditing && !areIdsEqual(activeComponentIds, fetchedComponentIds);
  const isDirty = isOrderDirty;
  const numberedComponents = useMemo(
    () =>
      computeComponentDisplayNumbers(
        activeComponents,
        isAutoNumberingDisabled(activeSettings),
      ),
    [activeComponents, activeSettings],
  );
  const dueSoonItems = useMemo(
    () =>
      buildDueSoonItems(
        numberedComponents,
        isDueSoonSectionDisabled(activeSettings),
      ),
    [activeSettings, numberedComponents],
  );
  const shouldShowDueSoonSummary = !isDueSoonSectionDisabled(activeSettings);
  const shouldShowControlPanel = course?.userRole === 'instructor';
  const shouldShowInstructorCard =
    course &&
    (course.userRole !== 'instructor' || !isInstructorCardDismissed);
  const shouldShowSidebar = Boolean(
    course && (shouldShowControlPanel || shouldShowDueSoonSummary),
  );

  useEffect(() => {
    if (!course || isEditing) {
      return;
    }

    setDraftComponents(course.components);
  }, [course, isEditing]);

  useEffect(() => {
    if (course?.userRole !== 'instructor') {
      setIsInstructorCardDismissed(false);
      return;
    }

    setIsInstructorCardDismissed(
      typeof window !== 'undefined' &&
        window.localStorage.getItem(INSTRUCTOR_CARD_DISMISSED_STORAGE_KEY) === 'true',
    );
  }, [course?.userRole]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  let announcement = getChapterCountLabel(components);

  if (!courseId) {
    announcement = 'Unable to determine which course to load.';
  } else if (courseQuery.isPending) {
    announcement = 'Loading course homepage.';
  } else if (courseQuery.isError && courseQuery.error.kind === 'auth') {
    announcement = 'Your session expired. Redirecting to sign in.';
  } else if (courseQuery.isError) {
    announcement = `Unable to load this course. ${getErrorMessage(courseQuery.error)}`;
  } else if (components.length === 0) {
    announcement = 'No course content available yet.';
  } else if (actionAnnouncement) {
    announcement = actionAnnouncement;
  }

  const startEditing = () => {
    if (!course) {
      return;
    }

    setDraftComponents(course.components);
    setActionAnnouncement('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (
      isDirty &&
      !window.confirm('Discard unsaved course homepage changes?')
    ) {
      return;
    }

    setDraftComponents(course?.components ?? []);
    setActionAnnouncement('');
    setIsEditing(false);
  };

  const saveEditing = async () => {
    if (!course || !courseId) {
      return;
    }

    await updateCourseHome.mutateAsync(
      // Deliberately no course_settings. UpdateCourse REPLACES that message
      // wholesale, so an edit-mode save carrying the settings it captured on
      // entry would silently revert anything changed in the Display Settings
      // dialog meanwhile.
      buildUpdateCourseHomePayload({
        courseId,
        orderedComponentUids: activeComponentIds,
        orderChanged: isOrderDirty,
      }),
    );
    setIsEditing(false);
    setActionAnnouncement('');
  };



  const dismissInstructorCard = () => {
    setIsInstructorCardDismissed(true);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(INSTRUCTOR_CARD_DISMISSED_STORAGE_KEY, 'true');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;

    if (!overId || activeId === overId) {
      return;
    }

    const fromIndex = draftComponents.findIndex((item) => item.id === activeId);
    const toIndex = draftComponents.findIndex((item) => item.id === overId);

    if (fromIndex < 0 || toIndex < 0) {
      return;
    }

    setDraftComponents((currentComponents) =>
      reorderCourseHomeItems(currentComponents, fromIndex, toIndex),
    );
  };

  const getComponentActions = (item: ComponentListItem) => {
    if (isEditing || course?.userRole !== 'instructor' || !courseId) {
      return null;
    }

    // A divider is a component with is_section_divider set, so it deletes
    // through the same path. Only Delete applies: there is no content to copy.
    if (item.kind === 'divider') {
      return (
        <ComponentKebabMenu
          component={
            {
              id: item.componentId ?? item.id,
              title: item.title,
              uid: item.componentUid ?? item.id,
            } satisfies RawComponent
          }
          componentId={item.componentId ?? item.id}
          componentTitle={item.title}
          componentUid={item.componentUid}
          currentCourseId={courseId}
          currentCourseTitle={course.title}
          itemKind="divider"
          onSuccess={setActionAnnouncement}
        />
      );
    }

    const rawComponent =
      item.rawComponent ??
      ({
        id: item.componentId ?? item.id,
        title: item.title,
        uid: item.componentUid ?? item.id,
      } satisfies RawComponent);

    return (
      <ComponentKebabMenu
        component={rawComponent}
        componentId={item.componentId ?? item.id}
        componentTitle={item.title}
        componentUid={item.componentUid}
        currentCourseId={courseId}
        currentCourseTitle={course.title}
        isLive={item.isLive}
        onSuccess={setActionAnnouncement}
      />
    );
  };

  const showStatusVisibilityIndicator = course?.userRole !== 'student';

  const renderInstructorCard = () => {
    if (!course || !shouldShowInstructorCard) {
      return null;
    }

    return (
      <Card className={styles.metaPanel} variant="metric">
        {course.userRole === 'instructor' ? (
          <button
            aria-label="Hide instructor card"
            className={styles.dismissInstructorCardButton}
            onClick={dismissInstructorCard}
            type="button"
          >
            <X aria-hidden="true" size={14} strokeWidth={2.2} />
          </button>
        ) : null}
        <p className={styles.metaLabel}>Instructor</p>
        <p className={styles.metaValue}>{course.instructorName}</p>
        {course.instructor?.email ? (
          <p className={styles.metaEmail}>
            <a href={`mailto:${course.instructor.email}`}>
              {course.instructor.email}
            </a>
          </p>
        ) : null}
      </Card>
    );
  };

  const renderListSegments = () => {
    const segments: ReactNode[] = [];
    let rows: ReactNode[] = [];
    let segmentIndex = 0;

    const flushRows = () => {
      if (rows.length === 0) {
        return;
      }

      segments.push(
        <Card className={styles.listSurface} key={`segment-${segmentIndex}`}>
          <ul className={styles.list}>{rows}</ul>
        </Card>,
      );
      rows = [];
      segmentIndex += 1;
    };

    numberedComponents.forEach(({ displayNumber, item }) => {
      if (item.kind === 'divider') {
        flushRows();
        segments.push(
          isEditing ? (
            <SortableCourseHomeItem
              displayNumber={displayNumber}
              item={item}
              key={`${item.kind}-${item.id}`}
              rowIndex={0}
              showStatusVisibilityIndicator={showStatusVisibilityIndicator}
            />
          ) : (
            <SectionDivider
              actions={getComponentActions(item)}
              item={item}
              key={`${item.kind}-${item.id}`}
            />
          ),
        );
        return;
      }

      const rowIndex = rows.length;

      rows.push(
        isEditing ? (
          <SortableCourseHomeItem
            displayNumber={displayNumber}
            item={item}
            key={`${item.kind}-${item.id}`}
            rowIndex={rowIndex}
            showStatusVisibilityIndicator={showStatusVisibilityIndicator}
          />
        ) : (
          <li
            className={styles.item}
            data-interactive="true"
            data-kind={item.kind}
            data-separated={rowIndex > 0 ? 'true' : 'false'}
            key={`${item.kind}-${item.id}`}
          >
            <ComponentCard
              actions={getComponentActions(item)}
              displayNumber={displayNumber}
              item={item}
              showStatusVisibilityIndicator={showStatusVisibilityIndicator}
            />
          </li>
        ),
      );
    });

    flushRows();

    return segments;
  };

  return (
    <section
      aria-labelledby="course-homepage-heading"
      className={styles.page}
      data-has-create-new={isInstructor ? 'true' : 'false'}
    >
      <div className={styles.layout}>
        <div
          className={styles.titleRow}
          data-has-instructor-card={shouldShowInstructorCard ? 'true' : 'false'}
        >
          <div className={styles.titleGroup}>
            <h1 className={styles.title} id="course-homepage-heading">
              {course?.title ?? 'Course'}
            </h1>
            {course ? (
              <EditCourseButton
                courseId={courseId}
                isEditing={isEditing}
                onEdit={startEditing}
                userRole={course.userRole}
              />
            ) : null}
          </div>
          {renderInstructorCard()}
        </div>

        <div
          className={styles.contentArea}
          data-has-sidebar={shouldShowSidebar ? 'true' : 'false'}
        >
          {shouldShowSidebar && course ? (
            <aside className={styles.sidebar}>
              {shouldShowControlPanel ? (
                <CourseControlPanel
                  actions={controlPanelActions}
                  course={course}
                  courseId={courseId}
                  onOpenRoster={handleOpenRoster}
                  rosterStudentCount={rosterQuery.data?.studentCount}
                  staffInvites={staffInvites}
                />
              ) : null}
              {shouldShowDueSoonSummary ? (
                <DueSoonSection items={dueSoonItems} />
              ) : null}
            </aside>
          ) : null}

          <div className={styles.mainColumn}>
            <p aria-atomic="true" aria-live="polite" className={styles.liveRegion}>
              {announcement}
            </p>

            {updateCourseHome.isError ? (
              <Card className={styles.stateCard} role="alert">
                <h2 className={styles.stateTitle}>Unable to save course changes</h2>
                <p className={styles.stateMessage}>
                  {getErrorMessage(updateCourseHome.error)}
                </p>
              </Card>
            ) : null}

            {!courseId ? (
              <Card className={styles.stateCard} role="alert">
                <h2 className={styles.stateTitle}>Missing course</h2>
                <p className={styles.stateMessage}>
                  This course link is incomplete, so the homepage could not be loaded.
                </p>
              </Card>
            ) : null}

            {courseQuery.isPending ? (
              <Card className={styles.stateCard} role="status">
                <h2 className={styles.stateTitle}>Loading course homepage</h2>
                <p className={styles.stateMessage}>
                  Gathering the course outline and preparing your component list.
                </p>
              </Card>
            ) : null}

            {courseQuery.isError && courseQuery.error.kind === 'auth' ? (
              <Card className={styles.stateCard} role="status">
                <h2 className={styles.stateTitle}>Redirecting to sign in</h2>
                <p className={styles.stateMessage}>
                  Your session needs to be refreshed before this course can be shown.
                </p>
              </Card>
            ) : null}

            {courseQuery.isError && courseQuery.error.kind !== 'auth' ? (
              <Card className={styles.stateCard} role="alert">
                <h2 className={styles.stateTitle}>Unable to load this course</h2>
                <p className={styles.stateMessage}>{getErrorMessage(courseQuery.error)}</p>
                <Button
                  onClick={() => {
                    void courseQuery.refetch();
                  }}
                  variant="primary"
                >
                  Try again
                </Button>
              </Card>
            ) : null}

            {!courseQuery.isPending &&
            !courseQuery.isError &&
            course &&
            components.length === 0 ? (
              <Card className={styles.stateCard}>
                <h2 className={styles.stateTitle}>No components yet</h2>
                <p className={styles.stateMessage}>
                  Chapters and assignments will appear here when content is available.
                </p>
              </Card>
            ) : null}

            {!courseQuery.isPending &&
            !courseQuery.isError &&
            course &&
            components.length > 0 ? (
              <>
                <div className={styles.listStack} data-testid="component-list-surface">
                  {isEditing ? (
                    <DndContext
                      accessibility={{ announcements: dragAnnouncements }}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                      sensors={sensors}
                    >
                      <SortableContext
                        items={activeComponentIds}
                        strategy={verticalListSortingStrategy}
                      >
                        {renderListSegments()}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    renderListSegments()
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {course ? (
        <>
          {course.userRole === 'instructor' && isEditing ? (
            <div className={styles.editActionBar}>
              <button
                aria-disabled={updateCourseHome.isPending}
                className={styles.cancelEditButton}
                data-disabled={updateCourseHome.isPending ? 'true' : 'false'}
                onClick={() => {
                  if (updateCourseHome.isPending) {
                    return;
                  }

                  cancelEditing();
                }}
                type="button"
              >
                Cancel
              </button>
              <button
                aria-disabled={!isDirty || updateCourseHome.isPending}
                className={styles.saveEditButton}
                data-disabled={!isDirty || updateCourseHome.isPending ? 'true' : 'false'}
                onClick={() => {
                  if (!isDirty || updateCourseHome.isPending) {
                    return;
                  }

                  void saveEditing();
                }}
                type="button"
              >
                {updateCourseHome.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          ) : null}
          <CreateSectionFAB
            courseId={courseId}
            isEditing={isEditing}
            userRole={course.userRole}
          />
        </>
      ) : null}
    </section>
  );
}
